import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { fetchLayoutType } from 'src/hooks';
import type { FetchReturn, IAssembly } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapAllAssemblies } from './helpers';

export const useGetAssemblies = (filters: string[] = [], enabled = true) =>
  useInfiniteQuery({
    queryKey: queryKeys.assemblies.pagination(filters),
    queryFn: ({ pageParam = 1 }) => fetchAssemblies(pageParam, filters),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: enabled && !isEmpty(filters),
  });

const fetchAssemblies = async (page: number, filters: string[] = []) => {
  const [response, layoutTypes] = await Promise.all([
    requestCCApi({
      url: `/assemblies${generateQuery(
        ['layouts'],
        concat(filters, ['count=true', `page=${page}`]),
      )}`,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.layoutTypes.all(),
      queryFn: fetchLayoutType,
      staleTime: Infinity,
    }),
  ]);

  const assemblyIds = response?.data.map((assembly: IAssembly) =>
    assembly.id.toString(),
  );

  const childrenAssemblies = await queryClient.fetchQuery({
    queryKey: queryKeys.assemblies.all(
      `variation_parent_id.list=${assemblyIds.join('|')}`,
    ),
    queryFn: () =>
      fetchAssembliesWithoutPagination([
        `variation_parent_id.list=${assemblyIds.join('|')}`,
      ]),
    staleTime: Infinity,
  });

  const assembliesWithChildren = response?.data.map((assembly: IAssembly) => {
    return {
      ...assembly,
      childrenAssemblies: childrenAssemblies.filter(
        (childAssembly: IAssembly) =>
          childAssembly.variation_parent_id === assembly.id,
      ),
    };
  });

  return {
    assemblies: mapAllAssemblies(response, layoutTypes, assembliesWithChildren),
    totalPages: response?.totalPages ?? 0,
    totalCount: response?.totalCount ?? 0,
  };
};

export const useGetAssembliesWithoutPagination = (
  filters: string[] = [],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: queryKeys.assemblies.all(concat(filters, ['pageSize=0'])),
    queryFn: () => fetchAssembliesWithoutPagination(filters),
    enabled: enabled,
    staleTime: Infinity,
  });

export const fetchAssembliesWithoutPagination = async (
  filters: string[] = [],
) => {
  const [response, layoutTypes] = await Promise.all([
    requestCCApi({
      url: `/assemblies${generateQuery(
        ['layouts'],
        concat(filters, ['pageSize=0']),
      )}`,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.layoutTypes.all(),
      queryFn: fetchLayoutType,
      staleTime: Infinity,
    }),
  ]);

  return mapAllAssemblies(response, layoutTypes);
};

export const useGetAssembliesWithoutPaginationAndLayout = (
  filters: string[] = [],
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: queryKeys.assemblies.all(concat(filters, ['pageSize=0'])),
    queryFn: () => fetchAssembliesWithoutPaginationAndLayout(filters),
    enabled: enabled,
    staleTime: Infinity,
  });

export const fetchAssembliesWithoutPaginationAndLayout = async (
  filters: string[] = [],
) => {
  const response = await requestCCApi({
    url: `/assemblies${generateQuery(filters, ['pageSize=0'])}`,
  });

  return response.data;
};

export const useGetAssembliesWithoutInclude = (
  filters: string[],
  overrideQuerySetting?: UseQueryOptions<IAssembly[]>,
) => {
  return useQuery({
    queryKey: queryKeys.assemblies.all(filters),
    queryFn: () => fetchAssembliesWithoutInclude(concat(filters, 'pageSize=0')),

    ...overrideQuerySetting,
  });
};
export const fetchAssembliesWithoutInclude = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IAssembly[]>> => {
  const response = await requestCCApi({
    url: `/assemblies${generateQuery(
      [],
      concat(filters, isPagination ? 'count=true' : []),
    )}`,
  });

  const data = response.data;

  if (isPagination) {
    return {
      data: data,
      totalPages: response.totalPages ?? 0,
    } as FetchReturn<T, IAssembly[]>;
  }

  return data as FetchReturn<T, IAssembly[]>;
};
